"use strict";

// ---------------------------------------------------
// Dependencies

var _ = require('underscore');
var Backbone = require('dibs-backbone');
var checkProps = require('bunsen/helpers/checkProps');
var FieldRule = require('../rules/FieldRule');
var assert = require('bunsen/helpers/assert');

// ---------------------------------------------------
// Module Definition

/**
 * @class FieldValidation
 *
 * @param {object} options
 * @param {string} options.field Overrides property of the same name
 * @param {string} [options.fieldDisplayName] Overrides property of the same name
 * @param {boolean|function} [options.required=undefined] Overrides property of the same name
 * @param {function} [options.required] Overrides property of the same name
 * @param {object|FieldRule} [options.rule] Overrides property of the same name
 * @param {string} [options.ruleName] Overrides property of the same name
 * @param {object} [options.additionalRules]
 *      If you have multiple rules, use this option to pass in
 *      to the field schema.
 *
 * @property {string} field The path of the field relative to the validated root model
 * @property {string} fieldDisplayName
 *      A version of the field name that can be displayed to the user. This will be
 *      combined with a generic 'messageBody' property of a rule to create an
 *      error message to show the user. If a 'message' property is defined, that will
 *      override the automatic generation of a message with the fieldDisplayName and
 *      the messageBody
 * @property {boolean|function} required
 *      A boolean or function returning a boolean. Defaults to 'undefined'.
 * @property {boolean} autoRun
 *      Automatically run the validation when the {@link Validator} 'validate' method is called.
 * @property {object|FieldRule} rule
 *      A correctly formatted rule schema object or a FieldRule instance. Defaults to undefined.
 *      This field can be used if there is only one rule needed for a field. Otherwise you can
 *      specify all rules with 'additionalRules' option or the 'setRule' methods.
 * @property {string} ruleName
 *      A name to describe the rule. Defaults to the value of the 'field' property.
 * @property {string} ruleName
 *      A name to describe the rule. Defaults to the value of the 'field' property.
 */
var FieldValidation = module.exports = function () {
    this._rules = {};
    this.initialize.apply(this, arguments);
};

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(FieldValidation.prototype, {

    autoRun: true,

    requiredMessageBody: 'is required',

    /**
     * @protected
     * Override in extension. Passed options that
     * were passed to the constructor.
     */
    initialize: function (options) {
        options = options || {};
        var self = this;

        // The path of the field from the root model
        self.field = options.field || self.field;

        // The display value of the field
        self.fieldDisplayName = options.fieldDisplayName || self.fieldDisplayName;

        // A boolean or function returning a boolean that says if the field is required to have a value or not
        self.required = !_.isUndefined(options.required) ? options.required : self.required;

        // A callback function that overrides 'required'. Instead of a callback function with the context
        // being the root model, the callback context is the FieldValidation instance and the arguments
        // are model, fieldValue, field
        self.required = !_.isUndefined(options.required) ? options.required : self.required;

        // Automatically run the validation when Rule.validate is called. True by default
        self.autoRun = !_.isUndefined(options.autoRun) ? options.autoRun : self.autoRun;

        // If you have only one rule, you can set it with this property
        self.rule = options.rule || self.rule;

        // The name of the default rule, which will default to the field name
        self.ruleName = options.ruleName || self.ruleName || self.field;

        // If the user passed rules as an option
        if (options.additionalRules) {
            self.setRulesWithMap(options.additionalRules);
        }

        // Sets the main rule using the 'rule' and 'ruleName' properties
        self.setupDefaultRule();

        // set the message for required errors if provided
        self.requiredMessage = options.requiredMessage || self.requiredMessage;
        self.setupDefaultRequiredMessage();
    },

    /**
     * @param {string} name
     * @param {object|FieldRule} rule
     * @returns {FieldValidation}
     */
    setRuleByName: function (name, rule) {
        this._rules[name] = rule;
        assert(rule instanceof FieldRule || typeof rule === 'object', 'rule must be an instance Rule or be an object');
        if (rule instanceof FieldRule && !rule.getParentValidation()) {
            rule.setParentValidation(this);
        }
        return this;
    },

    /**
     * Get a rule for a particular name
     * @param {string} name
     * @returns {object|FieldRule}
     */
    getRuleByName: function (name) {
        return this._rules[name];
    },

    /**
     * @param {object} map
     * @returns {FieldValidation}
     */
    setRulesWithMap: function (map) {
        var name;
        for (name in map) {
            if (map.hasOwnProperty(name)) {
                this.setRuleByName(name, map[name]);
            }
        }
        return this;
    },

    /**
     * @param {boolean} required
     * @returns {FieldValidation}
     */
    setRequired: function (required) {
        this.required = required;
        return this;
    },

    /**
     * @param {boolean} autoRun
     * @returns {FieldValidation}
     */
    setAutoRun: function (autoRun) {
        this.autoRun = autoRun;
        return this;
    },

    /**
     * @protected
     * @returns {FieldValidation}
     */
    setupDefaultRule: function () {
        if (this.rule) {
            this.setRuleByName(this.ruleName, this.rule);
        }
        return this;
    },

    /**
     * @param {string} name
     * @param {string} message
     * @returns {FieldValidation}
     */
    setMessageForRule: function (name, message) {
        var rule = this.getRuleByName(name);
        console.assert(rule, 'Rule for name "' + name + '" doesn\'t exist, so a message cannot be set');
        rule.setMessage(message);
        return this;
    },

    /**
     * @returns {string}
     */
    getMessageForRequired: function () {
        return this.requiredMessage;
    },

    /**
     * @param {string} message
     * @returns {FieldValidation}
     */
    setRequiredMessage: function (message) {
        this.requiredMessage = message;
        return this;
    },

    /**
     * @protected
     * @returns {FieldValidation}
     */
    setupDefaultRequiredMessage: function () {
        if (this.requiredMessage) {
            this.setRequiredMessage(_.result(this, 'requiredMessage'));
        } else if (this.fieldDisplayName) {
            this.setRequiredMessage(this.createMessageWithBody(this.requiredMessageBody));
        }
        return this;
    },

    /**
     * @param {string} name Rule name
     * @returns {string}
     */
    getMessageForRule: function (name) {
        var rule = this.getRuleByName(name);
        return rule ? _.result(rule, 'message') : null;
    },

    /**
     * Invoked by {@link Validator} in order
     * to determine if this field has a real
     * value or not (aka not empty string, null
     * or undefined)
     * @param {Backbone.Model} model
     * @param {*} val
     * @returns {boolean}
     */
    hasRealValue: function (model, val) {
        return (parseInt(val, 10) === 0 || !!val);
    },

    /**
     * Invoked by the {@link Validator} class
     * to get the value of the field for a model
     * @param {Backbone.Model} model
     * @param {string} fieldName
     * @returns {*}
     */
    getModelValueForField: function (model, fieldName) {
        return model.get(fieldName);
    },

    /**
     * Return a messageBody for a rule if it is defined
     * @param {string} name
     * @returns {string}
     */
    getMessageBodyForRule: function (name) {
        var rule = this.getRuleByName(name);
        return rule ?  _.result(rule, 'messageBody') : null;
    },

    /**
     * Get a default message for a rule if
     * the rule is defined.
     * @param {string} name
     * @returns {string}
     */
    getDefaultMessageForRule: function (name) {
        var rule = this.getRuleByName(name);
        return rule ?  _.result(rule, 'defaultMessage') : null;
    },

    /**
     * Given the fieldDisplay name and a standard
     * validation error message body, returns a
     * display message for the user.
     * @param {string} messageBody
     * @returns {string}
     */
    createMessageWithBody: function (messageBody) {
        return FieldValidation.createMessage(this.fieldDisplayName, messageBody);
    },

    /**
     * Creates a formatted schema object for
     * the @{link Validator} instance.
     * @returns {object}
     */
    outputSchema: function () {

        var schema = {
            field: this.field,
            required: _.isFunction(this.required) ? _.bind(this.required, this) : this.required,
            autoRun: this.autoRun,
            messages: {},
            rules: this._rules ? {} : null
        };

        if (this.required) {
            schema.messages.required = this.getMessageForRequired();
        }

        _.each(this._rules, function (rule, name) {

            var message;
            var messageBody;

            schema.rules[name] = (_.isFunction(rule.outputSchema)) ? rule.outputSchema() : rule;

            message = this.getMessageForRule(name);
            messageBody = this.getMessageBodyForRule(name);

            if (!message && this.fieldDisplayName && messageBody) {
                message = this.createMessageWithBody(messageBody);
            }

            // If there is no message, use a default message for the rule if it is invalid
            schema.messages[name] = message || this.getDefaultMessageForRule(name);

        }, this);

        checkProps({
            field: 'string',
            required: ['boolean', 'function'],
            autoRun: 'boolean',
            messages: 'object',
            rules: 'object'
        }, schema);

        return schema;
    }

}, Backbone.Events);

// ---------------------------------------------------
// Define Module Static Properties

_.extend(FieldValidation, {

    /**
     * @function
     * @param {object} instanceProperties
     * @param {object} staticProperties
     * @returns {function} New constructor function
     */
    extend: Backbone.Model.extend,

    /**
     * @param {string} fieldDisplayName
     * @param {string} messageBody
     * @returns {string}
     */
    createMessage: function (fieldDisplayName, messageBody) {
        return "\"" + fieldDisplayName + "\" field " + messageBody;
    }

});


//////////////////
// WEBPACK FOOTER
// ./~/bunsen/validation/deprecated/field-validations/FieldValidation.js
// module id = 75
// module chunks = 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 46 47 48 49 50 51 52 53 54 55